import http from 'node:http';
import type { Request, Response } from 'express';

interface HealthCheckRouterOptions {
  /** 目标服务端口，默认 3000 */
  targetPort?: number;
  /** 目标服务主机，默认 localhost */
  targetHost?: string;
  /** 健康检查超时时间（毫秒），默认 2000ms */
  timeout?: number;
}

/**
 * 检查目标服务是否可用
 */
function checkServiceHealth(
  host: string,
  port: number,
  timeout: number
): Promise<{ available: boolean; responseTime?: number; error?: string }> {
  return new Promise((resolve) => {
    const startTime = Date.now();

    const req = http.request(
      {
        hostname: host,
        port,
        path: '/',
        method: 'HEAD',
        timeout,
      },
      (_res) => {
        const responseTime = Date.now() - startTime;
        // 任何响应都表示服务可用（包括错误状态码）
        resolve({
          available: true,
          responseTime,
        });
      }
    );

    req.on('timeout', () => {
      req.destroy();
      resolve({
        available: false,
        error: 'Request timeout',
      });
    });

    req.on('error', (err) => {
      resolve({
        available: false,
        error: err.message,
      });
    });

    req.end();
  });
}

/**
 * 创建健康检查路由
 */
export function createHealthCheckHandler(options: HealthCheckRouterOptions = {}) {
  const {
    targetPort = Number(process.env.SERVER_PORT) || 3000,
    targetHost = 'localhost',
    timeout = 2000,
  } = options;
  return  async (_req: Request, res: Response) => {
    try {
      const result = await checkServiceHealth(targetHost, targetPort, timeout);

      if (result.available) {
        res.status(200).json({
          status: 'healthy',
          service: `${targetHost}:${targetPort}`,
          responseTime: result.responseTime,
          timestamp: new Date().toISOString(),
        });
      } else {
        res.status(503).json({
          status: 'unhealthy',
          service: `${targetHost}:${targetPort}`,
          error: result.error,
          timestamp: new Date().toISOString(),
        });
      }
    } catch (error) {
      res.status(500).json({
        status: 'error',
        service: `${targetHost}:${targetPort}`,
        error: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString(),
      });
    }
  };
}
